import { createContext, useContext, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import {
  X, Play, Lock, ArrowRight, ShieldCheck, Boxes, Scan, Image as ImageIcon, MapPin,
  CalendarDays, Clock, Pencil, CheckCircle2,
} from "lucide-react";
import api from "@/lib/api";
import { getAttribution } from "@/lib/track";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Picture } from "@/components/public/Picture";
import { useLead } from "./LeadDialog";

const VTContext = createContext(null);
export const useVirtualTour = () => useContext(VTContext);

const SPACE_TYPES = ["Coworking", "BTS", "Managed Office", "Meeting Room", "Training Room"];
const TOKEN_KEY = "cz_vt_token";
const CONTACT_KEY = "cz_vt_contact";
const TIME_SLOTS = (() => {
  const out = [];
  for (let m = 10 * 60; m <= 18 * 60; m += 30) {
    const h = Math.floor(m / 60), mm = m % 60;
    const ampm = h >= 12 ? "PM" : "AM";
    const h12 = ((h + 11) % 12) + 1;
    out.push(`${h12}:${mm === 0 ? "00" : mm} ${ampm}`);
  }
  return out;
})();
const FEATURES = [
  [Scan, "Interactive Walkthrough"],
  [ImageIcon, "Real Spaces & Amenities"],
  [MapPin, "Experience the Environment"],
];

export function TourOverlay({ onClick, testid = "explore-360-btn" }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid}
      className="absolute inset-0 z-10 flex flex-col items-center justify-center group">
      <span className="absolute inset-0 bg-slate-900/20 group-hover:bg-slate-900/30 transition-colors duration-300" />
      <span className="relative w-16 h-16 rounded-full bg-white/95 text-primary flex items-center justify-center shadow-xl group-hover:scale-110 transition-transform duration-300">
        <Play className="w-7 h-7 ml-0.5 fill-primary" />
      </span>
      <span className="relative mt-3 text-white font-semibold text-sm drop-shadow-md">Explore in 360°</span>
      <span className="absolute bottom-3 right-3 inline-flex items-center gap-1.5 bg-slate-900/80 backdrop-blur text-white text-xs font-medium px-3 py-1.5 rounded-full">
        <Boxes className="w-3.5 h-3.5" /> Virtual Walkthrough
      </span>
    </button>
  );
}

export function VirtualTourProvider({ children }) {
  const { openLead } = useLead();
  const [formOpen, setFormOpen] = useState(false);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [wt, setWt] = useState(null);
  const [tourUrl, setTourUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", message: "", space_type: "Coworking" });
  // Physical tour (quick) form state
  const [physOpen, setPhysOpen] = useState(false);
  const [phBusy, setPhBusy] = useState(false);
  const [editContact, setEditContact] = useState(false);
  const [phForm, setPhForm] = useState({ visit_date: "", visit_time: "" });
  const [contact, setContact] = useState(null);
  const todayStr = new Date().toISOString().slice(0, 10);

  const openTour = useCallback(async (item) => {
    if (!item?.location_slug) return;
    setWt(item);
    const token = sessionStorage.getItem(TOKEN_KEY);
    if (token) {
      try {
        const { data } = await api.get(`/public/virtual-tour/${item.location_slug}`, { params: { token } });
        setTourUrl(data.tour_url);
        setViewerOpen(true);
        return;
      } catch {
        sessionStorage.removeItem(TOKEN_KEY);
      }
    }
    setForm({ name: "", email: "", phone: "", company: "", message: "", space_type: "Coworking" });
    setFormOpen(true);
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.phone.trim()) return toast.error("Please add your name, work email and phone number.");
    setBusy(true);
    try {
      const { data } = await api.post("/public/virtual-tour/unlock", {
        ...form,
        location_slug: wt.location_slug,
        ...getAttribution(),
      });
      sessionStorage.setItem(TOKEN_KEY, data.token);
      const c = { name: form.name, email: form.email, phone: form.phone, company: form.company, space_type: form.space_type };
      setContact(c);
      sessionStorage.setItem(CONTACT_KEY, JSON.stringify(c));
      setTourUrl(data.tour_url);
      setFormOpen(false);
      setViewerOpen(true);
      toast.success("Enjoy your 360° tour!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const closeViewer = () => { setViewerOpen(false); setTourUrl(""); };

  const bookPhysical = () => {
    let c = contact;
    if (!c) { try { c = JSON.parse(sessionStorage.getItem(CONTACT_KEY) || "null"); } catch { c = null; } }
    if (!c || !c.name || !c.email) {
      const slug = wt?.location_slug;
      closeViewer();
      openLead("tour", { location: slug });
      return;
    }
    setContact(c);
    setEditContact(false);
    setPhForm({ visit_date: "", visit_time: "" });
    setPhysOpen(true);
  };

  const setCt = (k) => (e) => setContact((c) => ({ ...c, [k]: e.target.value }));

  const submitPhysical = async (e) => {
    e.preventDefault();
    if (!phForm.visit_date || !phForm.visit_time) return toast.error("Please pick a visit date and time.");
    if (!contact?.name || !contact?.email) return toast.error("Please add your name and email.");
    setPhBusy(true);
    try {
      await api.post("/public/leads", {
        name: contact.name, email: contact.email, phone: contact.phone || "", company: contact.company || "",
        space_type: contact.space_type || "", type: "tour", source: "Virtual Walkthrough",
        location_slug: wt?.location_slug || "", visit_date: phForm.visit_date, visit_time: phForm.visit_time,
        message: `Physical tour requested via 360° walkthrough for ${wt?.name || ""}.`,
        ...getAttribution(),
      });
      sessionStorage.setItem(CONTACT_KEY, JSON.stringify(contact));
      toast.success("Tour booked! Our team will confirm shortly.");
      setPhysOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setPhBusy(false);
    }
  };

  return (
    <VTContext.Provider value={{ openTour }}>
      {children}

      {formOpen && createPortal(
        <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center sm:p-4" data-testid="vt-inquiry-modal">
          <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={() => setFormOpen(false)} />
          <div className="relative w-full sm:max-w-4xl bg-white rounded-t-3xl sm:rounded-3xl overflow-hidden shadow-2xl max-h-[94vh] sm:max-h-[90vh] grid sm:grid-cols-2 animate-in slide-in-from-bottom-4 sm:fade-in duration-300">
            {/* Visual side — desktop */}
            <div className="relative hidden sm:block bg-slate-900">
              <Picture src={wt?.preview_image} alt={wt?.name || "Workspace"} className="absolute inset-0 w-full h-full object-cover opacity-90" />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 via-slate-900/20 to-slate-900/30" />
              <div className="absolute top-5 left-5">
                <span className="inline-flex items-center gap-1.5 bg-white/95 text-slate-900 text-xs font-bold px-3 py-1.5 rounded-full">
                  CoWork<span className="text-primary">Zen</span>
                </span>
              </div>
              <div className="absolute inset-x-0 bottom-0 p-6">
                <p className="text-white/80 text-sm font-medium mb-1">Explore in 360°</p>
                <p className="text-white font-display text-xl font-bold mb-5">{wt?.name}</p>
                <div className="grid grid-cols-3 gap-3">
                  {FEATURES.map(([Ic, label]) => (
                    <div key={label} className="text-center">
                      <span className="w-10 h-10 mx-auto rounded-full bg-white/15 backdrop-blur flex items-center justify-center text-white mb-2">
                        <Ic className="w-4.5 h-4.5" />
                      </span>
                      <p className="text-[11px] leading-tight text-white/85">{label}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Form side */}
            <div className="relative p-5 sm:p-7 overflow-y-auto">
              <button onClick={() => setFormOpen(false)} data-testid="vt-close-form"
                className="absolute top-4 right-4 w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center text-slate-500 transition-colors">
                <X className="w-4 h-4" />
              </button>

              {/* Mobile preview */}
              <div className="sm:hidden relative -mx-5 -mt-5 mb-4 h-36 bg-slate-900">
                <Picture src={wt?.preview_image} alt={wt?.name || "Workspace"} className="w-full h-full object-cover opacity-90" />
                <div className="absolute inset-0 bg-slate-900/30" />
                <span className="absolute bottom-3 left-4 text-white font-display font-bold">{wt?.name}</span>
              </div>

              <p className="eyebrow mb-2">Get Instant Access</p>
              <h3 className="font-display text-2xl font-extrabold text-slate-900 leading-tight">Explore This Space in 360°</h3>
              <p className="text-sm text-slate-500 mt-2">Share a few details and unlock the virtual walkthrough of {wt?.name}.</p>

              <form onSubmit={submit} className="mt-5 space-y-3">
                <div>
                  <p className="text-xs font-semibold text-slate-500 mb-1.5">Space type</p>
                  <div className="flex flex-wrap gap-2" data-testid="vt-space-types">
                    {SPACE_TYPES.map((st) => {
                      const active = form.space_type === st;
                      return (
                        <button type="button" key={st}
                          data-testid={`vt-space-${st.toLowerCase().replace(/ /g, "-")}`}
                          onClick={() => setForm((f) => ({ ...f, space_type: st }))}
                          className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${active ? "bg-primary text-white border-primary" : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"}`}>
                          {st}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <Input data-testid="vt-name" placeholder="Full name*" value={form.name} onChange={set("name")} />
                <Input data-testid="vt-email" type="email" placeholder="Work email*" value={form.email} onChange={set("email")} />
                <div className="grid grid-cols-2 gap-3">
                  <Input data-testid="vt-phone" placeholder="Phone number*" value={form.phone} onChange={set("phone")} />
                  <Input data-testid="vt-company" placeholder="Company" value={form.company} onChange={set("company")} />
                </div>
                <div className="flex items-center gap-2 h-10 px-3 rounded-md border border-input bg-slate-50 text-sm text-slate-600">
                  <MapPin className="w-4 h-4 text-primary shrink-0" />
                  <span className="truncate" data-testid="vt-location">{wt?.name}{wt?.sector ? ` — ${wt.sector}` : ""}</span>
                </div>
                <Textarea data-testid="vt-message" placeholder="Tell us more (optional)" value={form.message} onChange={set("message")} />
                <Button data-testid="vt-unlock-btn" type="submit" disabled={busy} className="w-full rounded-full h-12 gap-2 text-base">
                  <Lock className="w-4 h-4" /> {busy ? "Unlocking…" : "Unlock 360° Tour"} {!busy && <ArrowRight className="w-4 h-4" />}
                </Button>
                <p className="flex items-center justify-center gap-1.5 text-xs text-slate-400">
                  <ShieldCheck className="w-3.5 h-3.5" /> We respect your privacy. Your details are safe with us.
                </p>
              </form>
            </div>
          </div>
        </div>,
        document.body
      )}

      {viewerOpen && createPortal(
        <div className="fixed inset-0 z-[110] bg-black flex flex-col" data-testid="vt-viewer">
          <div className="flex items-center justify-between px-4 sm:px-6 h-14 bg-slate-900 text-white shrink-0">
            <div className="min-w-0">
              <p className="font-semibold text-sm truncate">{wt?.name}</p>
              {wt?.sector && <p className="text-[11px] text-white/60 truncate">{wt.sector}</p>}
            </div>
            <div className="flex items-center gap-2.5">
              <Button onClick={bookPhysical} data-testid="vt-book-physical" variant="outline"
                className="rounded-full h-9 px-4 text-sm bg-transparent border-white/30 text-white hover:bg-white hover:text-slate-900 gap-1.5">
                Book a Physical Tour <ArrowRight className="w-3.5 h-3.5" />
              </Button>
              <button onClick={closeViewer} data-testid="vt-close-viewer"
                className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
          <div className="flex-1 bg-black">
            {tourUrl ? (
              <iframe title={`${wt?.name} 360 tour`} src={tourUrl} className="w-full h-full border-0"
                allow="xr-spatial-tracking; gyroscope; accelerometer; fullscreen" allowFullScreen />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-white/50">Loading tour…</div>
            )}
          </div>
        </div>,
        document.body
      )}

      {physOpen && createPortal(
        <div className="fixed inset-0 z-[120] flex items-end sm:items-center justify-center sm:p-4" data-testid="physical-tour-modal">
          <div className="absolute inset-0 bg-slate-900/70 backdrop-blur-sm" onClick={() => setPhysOpen(false)} />
          <div className="relative w-full sm:max-w-md bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl p-5 sm:p-6 max-h-[94vh] overflow-y-auto animate-in slide-in-from-bottom-4 sm:fade-in duration-300">
            <button onClick={() => setPhysOpen(false)} data-testid="pt-close"
              className="absolute top-4 right-4 w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center text-slate-500 transition-colors">
              <X className="w-4 h-4" />
            </button>
            <p className="eyebrow mb-2">Schedule Your Visit</p>
            <h3 className="font-display text-2xl font-extrabold text-slate-900 leading-tight">Book a Physical Tour</h3>
            <p className="text-sm text-slate-500 mt-2">Pick a date and time — we'll confirm your guided visit.</p>

            {/* Auto-selected hub */}
            <div className="mt-4 flex items-center gap-2.5 p-3 rounded-2xl bg-primary/5 border border-primary/15">
              <span className="w-9 h-9 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0"><MapPin className="w-4 h-4" /></span>
              <div className="min-w-0">
                <p className="text-[11px] uppercase tracking-wide text-primary/70 font-semibold">Selected hub</p>
                <p className="font-semibold text-slate-900 text-sm truncate" data-testid="pt-hub">{wt?.name}{wt?.sector ? ` — ${wt.sector}` : ""}</p>
              </div>
            </div>

            {/* Contact summary / edit */}
            <div className="mt-3 rounded-2xl border border-slate-200 p-3">
              <div className="flex items-center justify-between mb-1">
                <p className="text-xs font-semibold text-slate-500 flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-green-500" /> Your details</p>
                <button type="button" data-testid="pt-edit-contact" onClick={() => setEditContact((v) => !v)}
                  className="text-xs font-semibold text-primary hover:underline inline-flex items-center gap-1">
                  <Pencil className="w-3 h-3" /> {editContact ? "Done" : "Edit details"}
                </button>
              </div>
              {editContact ? (
                <div className="space-y-2 mt-2">
                  <Input data-testid="pt-name" placeholder="Full name*" value={contact?.name || ""} onChange={setCt("name")} />
                  <Input data-testid="pt-email" type="email" placeholder="Work email*" value={contact?.email || ""} onChange={setCt("email")} />
                  <Input data-testid="pt-phone" placeholder="Phone number" value={contact?.phone || ""} onChange={setCt("phone")} />
                </div>
              ) : (
                <p className="text-sm text-slate-700" data-testid="pt-contact-summary">
                  <span className="font-medium">{contact?.name}</span>
                  <span className="text-slate-400"> · </span>{contact?.email}
                  {contact?.phone ? <><span className="text-slate-400"> · </span>{contact.phone}</> : null}
                </p>
              )}
            </div>

            <form onSubmit={submitPhysical} className="mt-3 space-y-3">
              <div>
                <label className="text-xs font-semibold text-slate-500 flex items-center gap-1.5 mb-1.5"><CalendarDays className="w-3.5 h-3.5" /> Visit date</label>
                <Input data-testid="pt-date" type="date" min={todayStr} value={phForm.visit_date}
                  onChange={(e) => setPhForm((f) => ({ ...f, visit_date: e.target.value }))} />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-500 flex items-center gap-1.5 mb-1.5"><Clock className="w-3.5 h-3.5" /> Visit time</label>
                <select data-testid="pt-time" value={phForm.visit_time}
                  onChange={(e) => setPhForm((f) => ({ ...f, visit_time: e.target.value }))}
                  className={`flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring ${phForm.visit_time ? "text-slate-900" : "text-slate-400"}`}>
                  <option value="" className="text-slate-900">Select a time slot</option>
                  {TIME_SLOTS.map((t) => <option key={t} value={t} className="text-slate-900">{t}</option>)}
                </select>
              </div>
              <Button data-testid="pt-submit" type="submit" disabled={phBusy} className="w-full rounded-full h-12 gap-2 text-base">
                <CalendarDays className="w-4 h-4" /> {phBusy ? "Booking…" : "Confirm Tour Booking"}
              </Button>
              <p className="flex items-center justify-center gap-1.5 text-xs text-slate-400">
                <ShieldCheck className="w-3.5 h-3.5" /> We respect your privacy. Your details are safe with us.
              </p>
            </form>
          </div>
        </div>,
        document.body
      )}
    </VTContext.Provider>
  );
}
